import { Draft } from '@reduxjs/toolkit';
import {
  IDataTableQueryFieldsFragment,
  IEventConditionFieldsFragment,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { INextStepType } from '../../cachedEntities';
import { assertCondition, byTypeName } from '../../../../utils/types';
import { IBuilder, IRow, RowType } from '../types';
import { defaultEventCondition, defaultStep } from '../defaults';
import { parseExpressionMeasureToBuilder } from '../../../../utils/builder/measures/parseExpressionMeasureToBuilder';
import { parseSimpleMeasure } from '../../../../utils/builder/measures/utils';

const getEventConditionsFromDataTableMeasures = (
  measures: IDataTableQueryFieldsFragment['measures'],
): Array<IEventConditionFieldsFragment[]> => {
  return measures.reduce<Array<IEventConditionFieldsFragment[]>>(
    (acc, measure) => {
      // Per requirements, taking into account only Simple measures
      if (measure.__typename === 'SimpleMeasure') {
        const { eventConditions } = measure;
        acc.push(eventConditions);
      }

      return acc;
    },
    [],
  );
};

const prepareDataTableToNextStep = (
  draft: Draft<IBuilder>,
  builderInfo: IDataTableQueryFieldsFragment,
  targetType: INextStepType,
) => {
  assertCondition(targetType !== QueryType.DataTable);
  const { measures, measuresProperties } = builderInfo;

  if (targetType === QueryType.Expression) {
    if (
      // In case of a single measure - carry over as is
      measures.length === 1 ||
      // In case when all measures are compound measures - we will
      // carry over only 1st one
      measures.every((measure) => measure.__typename === 'CompoundMeasure')
    ) {
      const measure = measures[0];
      const measureProperties = measuresProperties[0];

      const parsedValues = parseExpressionMeasureToBuilder(
        measure,
        measureProperties,
      );

      draft[RowType.measures] = parsedValues.measures;
      draft.resultFilter = parsedValues.resultFilter;
      draft.measureFormula = parsedValues.measureFormula;
    } else {
      // Eventually, carry over only simple measures
      draft[RowType.measures] = measures.reduce<IRow[]>(
        (acc, measure, measureIndex) => {
          if (measure.__typename === 'SimpleMeasure') {
            const measureProperties = measuresProperties[measureIndex];
            assertCondition(
              measureProperties.__typename === 'SimpleMeasureProperties',
            );

            acc.push(parseSimpleMeasure(measure, measureProperties));
          }

          return acc;
        },
        [],
      );
    }
  } else if (targetType === QueryType.Path) {
    const eventConditions = getEventConditionsFromDataTableMeasures(measures);

    const startingEventConditions = eventConditions[0]
      // filter all ANY event conditions
      .filter(
        (eventCondition) =>
          eventCondition.events.filter(byTypeName('AnyEvent')).length === 0,
      );

    if (startingEventConditions.length > 0) {
      draft[RowType.paths][0] = startingEventConditions;
    }

    if (eventConditions.length > 1) {
      draft[RowType.paths][1] = eventConditions[eventConditions.length - 1];
    }
  } else if (targetType === QueryType.Retention) {
    const eventConditions = getEventConditionsFromDataTableMeasures(measures);

    draft.retention.retentionConditions[0] = eventConditions[0];

    if (eventConditions.length > 1) {
      draft.retention.retentionConditions[1] =
        eventConditions[eventConditions.length - 1];
    }
  } else if (targetType === QueryType.Funnel) {
    const eventConditions = getEventConditionsFromDataTableMeasures(measures);

    if (eventConditions.length === 1) {
      eventConditions.push([defaultEventCondition()]);
    }

    draft[RowType.steps] = eventConditions.map((parsedEventCondition) => ({
      ...defaultStep(),
      eventConditions: parsedEventCondition,
    }));
  }
};

export default prepareDataTableToNextStep;
